import * as React from "react";
import { Input } from "@/components/ui/input";
import { useCallback } from "react";
import { formatCommaDecimalToDot, formatNumber } from "@/lib/utils/formatters";

export type NumberInputProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "type"
>;

export const NumberInput = React.forwardRef<HTMLInputElement, NumberInputProps>(
  ({ onBlur, onChange, value, defaultValue, ...props }, ref) => {
    const handleBlur = useCallback(
      (e: React.FocusEvent<HTMLInputElement>) => {
        const el = e.target;
        // Align typed value with DB/API decimal convention (`.`) before submit/validation.
        const next = formatCommaDecimalToDot(el.value);
        if (next !== el.value) {
          if (onChange) {
            onChange({
              ...e,
              target: { ...el, value: next },
              currentTarget: { ...el, value: next },
            } as React.ChangeEvent<HTMLInputElement>);
          } else {
            el.value = next;
          }
        }
        onBlur?.(e);
      },
      [onBlur, onChange],
    );

    const valueProps =
      value !== undefined
        ? { value: formatDisplayValue(value) }
        : defaultValue !== undefined
          ? { defaultValue: formatDisplayValue(defaultValue) }
          : {};

    return (
      <Input
        inputMode="decimal"
        {...props}
        type="text"
        ref={ref}
        {...valueProps}
        onBlur={handleBlur}
        onChange={onChange}
      />
    );
  },
);
NumberInput.displayName = "NumberInput";

const formatDisplayValue = (
  value: string | number | readonly string[] | null | undefined,
): string => {
  if (value === null || value === undefined || value === "") {
    return "";
  }
  if (Array.isArray(value)) {
    return value.length ? formatNumber(String(value[0]), "") : "";
  }
  return formatNumber(value as string | number, "");
};
